import { List } from 'immutable';

import AuthenticatedAPIClient, { NotFoundError } from '../lib/AuthenticatedAPIClient';
import trainTime from '../lib/TrainTime';
import DisruptionAlert from '../records/DisruptionAlert';
import DisruptionAlertMute from '../records/DisruptionAlertMute';
import ServiceChangeAlert from '../records/ServiceChangeAlert';

import { resolveAll, resolve } from '../helpers/LinkMapper';
import { formatDisruptionAlertResponse, formatDisruptionAlertMuteResponse } from '../helpers/DisruptionAlert';
import {
    DisruptionAlertsLimitExceededError,
    DisruptionAlertsSecurityCheckFailureError,
    DisruptionAlertsMuteTokenExpiredError,
    DisruptionAlertsAlreadyMutedError,
} from '../errors/DisruptionAlerts';

const DisruptionAlertService = {

    /**
     * @return {String}
     */
    getBaseUrl() {
        return '/ddm-alerts';
    },

    /**
     * @param {User} user
     * @return {String}
     */
    getAuthenticatedBaseUrl(user) {
        return `${this.getBaseUrl()}/customer/${user.id}`;
    },

    /**
     * @param {User} user
     * @return {DisruptionAlert[]}
     */
    async getAll(user) {
        try {
            const endpoint = `${this.getAuthenticatedBaseUrl(user)}/disruption-alerts`;
            const response = await AuthenticatedAPIClient.get(endpoint);

            const alerts = response['disruption-alerts'].map(alert => formatDisruptionAlertResponse(alert));

            return resolveAll(alerts).as(DisruptionAlert);
        } catch (error) {
            if (!(error instanceof NotFoundError)) {
                throw error;
            }
            return new List();
        }
    },

    /**
     * @param {User} user
     * @param {Order} order
     * @param {OrderTrip} orderTrip
     * @param {OrderJourney} orderJourney
     * @return {DisruptionAlert}
     */
    async createFromOrderJourney({ user, order, orderTrip, orderJourney, stationFacilityAlert, isEmailNotification, isCloudMessageNotification }) {
        try {
            const hasWidgetPreferences = isEmailNotification !== undefined || isCloudMessageNotification !== undefined;

            if (!hasWidgetPreferences) {
                const preferences = await DisruptionAlertService.getPreferences(user.id);
                isEmailNotification = preferences.isEmailNotification;
                isCloudMessageNotification = preferences.isCloudMessageNotification;
            }

            const hasNoPreferences = !isEmailNotification && !isCloudMessageNotification;
            const alert = DisruptionAlert.createFromOrderJourney({
                user,
                order,
                orderTrip,
                orderJourney,
                stationFacilityAlert,
                isEmailNotification: hasNoPreferences ? true : isEmailNotification,
                isCloudMessageNotification
            });
            const endpoint = `${this.getAuthenticatedBaseUrl(user)}/disruption-alerts`;
            const response = await AuthenticatedAPIClient.post(endpoint, alert.toJSON());
            return resolve(formatDisruptionAlertResponse(response)).as(DisruptionAlert);
        } catch (err) {
            const { response } = err;
            if (response && response.status === 422) {
                throw new DisruptionAlertsLimitExceededError();
            }
            throw err;
        }
    },

    /**
     * @param {User} user
     * @param {Order} order
     * @param {OrderTrip} orderTrip
     * @param {Object} formData
     * @return {DisruptionAlert}
     */
    async createFromBuilder({ user, order, orderTrip, formData, stationFacilityAlert }) {
        try {
            const preferences = await DisruptionAlertService.getPreferences(user.id);
            let { isEmailNotification, isCloudMessageNotification } = preferences;

            const hasNoPreferences = !isEmailNotification && !isCloudMessageNotification;
            if (hasNoPreferences) {
                isEmailNotification = true;
            }

            const alert = DisruptionAlert.createFromBuilder({ user, order, orderTrip, formData, stationFacilityAlert, isEmailNotification, isCloudMessageNotification });
            const endpoint = `${this.getAuthenticatedBaseUrl(user)}/disruption-alerts`;
            const response = await AuthenticatedAPIClient.post(endpoint, alert.toJSON());
            return resolve(formatDisruptionAlertResponse(response)).as(DisruptionAlert);
        } catch (err) {
            const { response } = err;
            if (response && response.status === 422) {
                throw new DisruptionAlertsLimitExceededError();
            }
            throw err;
        }
    },

    /**
     * @param {User} user
     * @param {Object} alertData
     * @return {DisruptionAlert}
     */
    async update(user, updatedAlert) {
        const endpoint = `${this.getAuthenticatedBaseUrl(user)}/disruption-alerts/${updatedAlert.id}`;
        const payload = updatedAlert.toJSON();
        delete payload.isJourneyAlert;
        const response = await AuthenticatedAPIClient.put(endpoint, payload);
        return resolve(formatDisruptionAlertResponse(response)).as(DisruptionAlert);
    },

    /**
     * @param {User} user
     * @param {DisruptionAlert} alert
     */
    async delete(user, alert) {
        const endpoint = `${this.getAuthenticatedBaseUrl(user)}/disruption-alert/${alert.id}`;
        await AuthenticatedAPIClient.delete(endpoint);
        return true;
    },

    /**
     * @param {User} user
     * @param {DisruptionAlert} alert
     * @param {DisruptionAlertMute} alertMute
     */
    async createMute(user, alert, alertMute) {
        const endpoint = `${this.getAuthenticatedBaseUrl(user)}/disruption-alert/${alert.id}/alert-mute`;
        const response = await AuthenticatedAPIClient.post(endpoint, alertMute.toJSON());
        return resolve(formatDisruptionAlertMuteResponse(response)).as(DisruptionAlertMute);
    },

    /**
     * @param {User} user
     * @param {DisruptionAlert} alert
     * @param {DisruptionAlertMute} alertMute
     */
    async editMute(user, alert, alertMute) {
        const endpoint = `${this.getAuthenticatedBaseUrl(user)}/disruption-alert/${alert.id}/alert-mute/${alertMute.id}`;
        const response = await AuthenticatedAPIClient.put(endpoint, alertMute.toJSON());
        return resolve(formatDisruptionAlertMuteResponse(response)).as(DisruptionAlertMute);
    },

    /**
     * @param {User} user
     * @param {DisruptionAlert} alert
     * @param {DisruptionAlertMute} alertMute
     */
    async deleteMute(user, alert, alertMute) {
        const endpoint = `${this.getAuthenticatedBaseUrl(user)}/disruption-alert/${alert.id}/alert-mute/${alertMute.id}`;
        await AuthenticatedAPIClient.delete(endpoint);
        return true;
    },

    /**
     * @param {Number} ddmUserId
     * @param {Number} alertId
     * @param {String} token
     * @param {Boolean} muteAll
     */
    async muteForDay(customerId, disruptionAlertId, securityToken, muteAllAlerts) {
        try {
            const endpoint = `${this.getBaseUrl()}/alert-mute-for-day`;
            await AuthenticatedAPIClient.post(endpoint, {
                customerId,
                disruptionAlertId,
                securityToken,
                muteAllAlerts,
            });
            return true;
        } catch (err) {
            const { response } = err;
            if (response) {
                switch (response.status) {
                    case 400:
                        throw new DisruptionAlertsSecurityCheckFailureError();
                    case 403:
                        throw new DisruptionAlertsMuteTokenExpiredError();
                    case 409:
                        throw new DisruptionAlertsAlreadyMutedError();
                }
            }
            throw err;
        }
    },

    async getBankHolidays() {
        try {
            const endpoint = `${this.getBaseUrl()}/bank-holidays?excludePastDates=true`;
            const response = await AuthenticatedAPIClient.get(endpoint);
            const dates = response['england-and-wales'].map(({ date }) => date);
            return resolveAll(dates).with(trainTime);
        } catch (error) {
            if (!(error instanceof NotFoundError)) {
                throw error;
            }
            return new List();
        }
    },

    async createServiceChangeAlert({ timetableChangesAlert }) {
        try {
            const endpoint = `${this.getBaseUrl()}/service-change`;
            await AuthenticatedAPIClient.post(endpoint, timetableChangesAlert);
        } catch (err) {
            console.warn('createServiceChangeAlert err', err);
            throw err;
        }
    },

    async deleteServiceChangeAlert(user, alrt) {
        const endpoint = `${this.getAuthenticatedBaseUrl(user)}/service-change/${alrt.serviceChangeAlertId}`;
        await AuthenticatedAPIClient.delete(endpoint);
        return true;
    },

    // eslint-disable-next-line
    async deleteServiceChangeAlertAnon({ alertId, ddmCustomerId, orderId, securityToken, ticketIds }) {
        try {
            const endpoint = `${this.getBaseUrl()}/service-change-alert-remove`;
            if (orderId) {
                await AuthenticatedAPIClient.post(endpoint, {
                    orderId,
                    securityToken,
                    ticketIds,
                });
            } else {
                await AuthenticatedAPIClient.post(endpoint, {
                    alertId,
                    deleteThisAlert: true,
                    ddmCustomerId,
                    securityToken,
                });
            }
            return true;
        } catch (err) {
            throw err;
        }
    },

    async getServiceChangeAlerts(user) {
        try {
            const endpoint = `${this.getAuthenticatedBaseUrl(user)}/service-change`;
            const response = await AuthenticatedAPIClient.get(endpoint);
            return resolveAll(response['service-changes']).as(ServiceChangeAlert);
        } catch (err) {
            throw err;
        }
    },

    async getPreferences(customerId) {
        const endpoint = `${this.getBaseUrl()}/customer/${customerId}/preference`;
        const response = await AuthenticatedAPIClient.get(endpoint);
        return response;
    },

    async setPreferences(customerId, prefs) {
        const endpoint = `${this.getBaseUrl()}/customer/${customerId}/preference`;
        const response = await AuthenticatedAPIClient.put(endpoint, prefs);
        return response;
    }

};

export default DisruptionAlertService;
